import {
  generateHouseHoldNumberCascader,
  Option,
} from '@/utils/generateHouseHoldNumberCascader';
import { Table, Cascader, TableProps, Button } from 'antd';
import axios from 'axios';
import React, { useState, useEffect, useMemo, useContext } from 'react';

import { Tab } from '@alifd/next';
import { Box } from '@mui/material';
import { PostalRegistration } from '@/types/database/postalRegistration';
import { DepositRegistration } from '@/types/database/depositRegistration';
import { BackStageContext } from '@/contexts/BackStageContext';
import PageHeader from '@/components/PageHeader';
import useService from '@/lib/hooks/useService';

import { TableContainer } from './styles';
import { getSorter } from './utils';

const DeliveryList: React.FC = () => {
  const [deposit, updateDeposit] = useService<DepositRegistration>({
    collectionName: 'deposit',
  });
  const [postal, updatePostal] = useService<PostalRegistration>({
    collectionName: 'postalRegistration',
  });

  const { backStageUpdateTrigger } = useContext(BackStageContext);
  const [filterHouseOptions, setFilterHouseOptions] = useState<Option[]>([]);
  const [filter, setFilter] = useState<{
    house: string[];
  }>({
    house: [],
  });
  const [filteredPostalData, setFilteredPostalData] = useState<
    PostalRegistration[]
  >([]);
  const [filteredDepositData, setFilteredDepositData] = useState<
    DepositRegistration[]
  >([]);
  const [tabStatus, setTabStatus] = useState<'安排中' | '待安排'>('安排中');
  useEffect(() => {
    updateDeposit();
    updatePostal();
  }, [backStageUpdateTrigger, updateDeposit, updatePostal]);

  useEffect(() => {
    const newHouseNumbers: string[] = [];
    postal.forEach((pile) => {
      if (typeof pile.household === 'string') {
        newHouseNumbers.push(pile.household);
      }
    });
    setFilterHouseOptions(
      generateHouseHoldNumberCascader(
        newHouseNumbers.sort((a, b) => a.localeCompare(b))
      )
    );
  }, [postal]);

  useEffect(() => {
    const newPostalData = postal
      .filter((pile) => pile.robotDelivery === true)
      .filter((pile) => {
        if (
          filter.house &&
          filter.house.length > 0 &&
          pile.household !== filter.house.join('-')
        ) {
          return false;
        }

        return true;
      });
    const newDepositData = deposit
      .filter((pile) => pile.robotDelivery === true)
      .filter((pile) => {
        if (
          filter.house &&
          filter.house.length > 0 &&
          pile.household !== filter.house.join('-')
        ) {
          return false;
        }

        return true;
      });
    setFilteredPostalData(newPostalData);
    setFilteredDepositData(newDepositData);
  }, [filter, postal, deposit]);

  const columns: TableProps<PostalRegistration>['columns'] = useMemo(
    () => [
      {
        title: '任務順序',
        render: (_value, _record, index) => index + 1,
      },
      {
        title: '類型',
        dataIndex: 'packageType',
        sorter: getSorter('packageType'),
        render: (value) => (value ? '郵件包裹' : '寄物'),
      },
      {
        title: '包裹編號',
        dataIndex: 'order',
        sorter: getSorter('order'),
      },
      {
        title: '戶號',
        dataIndex: 'household',
        sorter: getSorter('household'),
      },

      {
        title: '收件人',
        dataIndex: 'recipient',
        sorter: getSorter('recipient'),
      },
      {
        title: '狀態',
        dataIndex: 'status',
        sorter: getSorter('status'),
      },
    ],
    []
  );
  const columnsFilter: TableProps<PostalRegistration>['columns'] = useMemo(
    () => [
      {
        title: '類型',
        dataIndex: 'packageType',
        sorter: getSorter('packageType'),
        render: (value) => (value ? '郵件包裹' : '寄物'),
      },
      {
        title: '包裹編號',
        dataIndex: 'order',
        sorter: getSorter('order'),
      },
      {
        title: '戶號',
        dataIndex: 'household',
        sorter: getSorter('household'),
      },
      {
        title: '收件人',
        dataIndex: 'recipient',
        sorter: getSorter('recipient'),
      },
      {
        title: '狀態',
        dataIndex: 'status',
        sorter: getSorter('status'),
      },
    ],
    []
  );

  const sortArrange = (
    a: PostalRegistration | DepositRegistration,
    b: PostalRegistration | DepositRegistration
  ) => {
    const arrangeA = a.arrange;
    const arrangeB = b.arrange;

    if (arrangeA) {
      return -1;
    }
    if (arrangeB) {
      return 1;
    }
    return 0;
  };

  const handleTabChange = (key: string | number) => {
    if (key === '安排中' || key === '待安排') {
      setTabStatus(key);
    } else {
      console.error('Error: type of key is wrong; activeKey:', key);
    }
  };

  return (
    <Box>
      {/* 標題 */}
      <PageHeader
        title="送件服務列表"
        breadcrumbs={[{ name: '送件服務' }, { name: '送件服務列表' }]}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          marginBottom: '10px',
        }}
      >
        <Cascader
          value={filter.house}
          onChange={(value) =>
            setFilter({
              ...filter,
              house: value ? value.map((v) => v.toString()) : [],
            })
          }
          options={filterHouseOptions}
          placeholder="請選擇戶號"
          allowClear
          style={{ width: 200 }}
        />
        {/* 20221114 暫時將呼叫機器人註解 展覽用不到 */}
        <Button
          style={{ marginLeft: 10 }}
          onClick={() => {
            try {
              axios.delete('/ezcon/api/robot/robotTask', {
                headers: {
                  authorization: localStorage.getItem('feathers-jwt'),
                },
              });
            } catch (error) {
              console.error(error);
            }
          }}
        >
          機器人緊急按鈕
        </Button>
      </Box>
      <Tab
        onChange={(key) => handleTabChange(key)}
        style={{ marginBottom: 10 }}
      >
        <Tab.Item title="安排中" key="安排中" />
        <Tab.Item title="待安排" key="待安排" />
      </Tab>
      <TableContainer>
        {/* {tabStatus === '安排中' ? ( */}
        <Table
          bordered
          dataSource={[...filteredPostalData, ...filteredDepositData]
            .sort(sortArrange)
            .filter((item) =>
              tabStatus === '安排中'
                ? item.robotDelivery &&
                  item.status !== '未領取' &&
                  item.status !== '已領取'
                : item.robotDelivery && item.status === '未領取'
            )}
          columns={tabStatus === '安排中' ? columns : columnsFilter}
          showSorterTooltip={false}
          pagination={{
            pageSize: 10,
          }}
          sortDirections={['descend', 'ascend', 'descend']}
        />
      </TableContainer>
    </Box>
  );
};

export default DeliveryList;
